import React, { useState, useCallback } from 'react';
import { Search, Loader2, AlertCircle, MessageSquare } from 'lucide-react';
import { chronologyApi, type ChronologyQueryResponse } from '../services/chronologyApi';

interface ChronologyQueryBarProps {
  chronologyId: string;
  onQueryResult: (result: ChronologyQueryResponse) => void;
  onOpenAssistant?: (prefill: string) => void;
  disabled?: boolean;
  eventCount?: number;
  filteredEventCount?: number;
}

export const ChronologyQueryBar: React.FC<ChronologyQueryBarProps> = ({
  chronologyId,
  onQueryResult,
  onOpenAssistant,
  disabled = false,
  eventCount,
  filteredEventCount,
}) => {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redirectHint, setRedirectHint] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed || !chronologyId || isLoading) return;

    setIsLoading(true);
    setError(null);
    setRedirectHint(null);

    try {
      const result = await chronologyApi.queryChronology(chronologyId, trimmed);

      // If the LLM classified this as SUMMARISE or CONSOLIDATE, redirect to assistant
      if (result.intent === 'SUMMARISE' || result.intent === 'CONSOLIDATE') {
        setRedirectHint(trimmed);
        return;
      }

      onQueryResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to filter timeline. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [query, chronologyId, isLoading, onQueryResult]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const placeholder = filteredEventCount != null && eventCount != null && filteredEventCount < eventCount
    ? `${filteredEventCount} of ${eventCount} events \u2014 ask anything, e.g. \u201Cshow only surgical events\u201D`
    : `Ask in plain English \u2014 e.g. \u201Chospital visits in 2024\u201D or \u201Cevents involving Dr Smith\u201D`;

  return (
    <div>
      <div className="flex items-center gap-2 px-3 py-2 bg-primary-50/50 dark:bg-primary-900/10 border border-primary-200 dark:border-primary-800/40 rounded-xl">
        <Search className="h-4 w-4 text-primary-400 dark:text-primary-500 flex-shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setRedirectHint(null); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className="flex-1 bg-transparent py-1 text-sm text-medical-900 dark:text-medical-100 placeholder-medical-400 dark:placeholder-medical-500 focus:outline-none disabled:opacity-50"
        />
        <button
          onClick={handleSubmit}
          disabled={!query.trim() || disabled || isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:bg-medical-300 dark:disabled:bg-medical-700 text-white rounded-lg text-xs font-medium transition-colors disabled:cursor-not-allowed flex-shrink-0"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Filtering...</span>
            </>
          ) : (
            <span>Filter</span>
          )}
        </button>
      </div>

      {error && (
        <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {redirectHint && (
        <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <MessageSquare className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            This looks like a question for the ClinLexis Assistant.{' '}
            {onOpenAssistant ? (
              <button
                onClick={() => onOpenAssistant(redirectHint)}
                className="font-medium underline hover:no-underline text-primary-600 dark:text-primary-400"
              >
                Ask the assistant instead
              </button>
            ) : (
              <span>Use the <strong>Ask</strong> sidebar to summarise, analyse, or ask questions about your timeline.</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};
